import { appConfig, UseCase } from '../service/config';
import { thirdPartyMediatorService } from '../third-party/service';
import { router } from '../service/router';
import * as Logger from '@navigator/frontend-logger';
import { tracklib } from '../service/tracker';
import { getModalLayer, NavModalLayer } from './nav-modal-layer';

export const ACTIONS_MENU_PRIMARY_SELECTOR = '#actions-menu-primary';
export const ACTIONS_MENU_HIDDEN_SELECTOR = '#actions-menu-hidden';

export const ACTIVE_ITEM_CLASS = 'navigator__item--active';
export const ACTIVE_ITEM_MORE_ACTIVE_CLASS = 'navigator__item--active-more-active';

export const NAVIGATOR_ITEM_MORE_ACTIVE_CLASS = 'navigator__item--more--active';
export const ACTIONS_MENU_HIDDEN_OPEN_CLASS = 'actions-menu__hidden--open';

/**
 * The NavActionsMenu handles clicks on Navigator icons and the visibility of the sub menu (AKA overflow menu)
 */
export class NavActionsMenu extends HTMLElement {
  private trackedApps: { [key: string]: boolean } = {};
  private modalLayer: NavModalLayer;
  private alertsDashboard: HTMLElement & { closeAlertsDashboard: () => void };
  private alertsDashboardIsOpen = false

  connectedCallback(): void {
    this.modalLayer = getModalLayer();
    this.alertsDashboard = document.querySelector('alertsdashboard-root');

    router.on('change', this.onRouteChange.bind(this));

    Array.from(this.querySelectorAll('[data-nav=menu-item]')).forEach((el: Element) => {
      const item = (el as HTMLElement);
      item.addEventListener('click', this.onItemClick.bind(this));
    });

    this.handleItems();

    window.addEventListener('resize', () => {
      this.handleItems();
    });

    const logoVariants = [].map.call(
      document.querySelectorAll('[data-svg=logo] symbol'),
      (symbol: HTMLElement) => symbol.id.split('_')[1],
    );

    /* istanbul ignore next else */
    if (logoVariants.indexOf('small') !== -1) {
      const smallLogo = this.querySelector('#actions-menu-brand-logo use');
      /* istanbul ignore next else */
      if (smallLogo) {
        smallLogo.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '#brandLogo_small');
      }
    }

    this.modalLayer.addEventListener('update', this.onModalLayerClick.bind(this));

    document.documentElement.addEventListener('updateTheme', (event: CustomEvent) => {
      this.alertsDashboard?.setAttribute('theme', event.detail)
    })
    this.alertsDashboard?.addEventListener('showNavItem', (event) => { event.stopPropagation(); this.showAlertsDashboardItem() }, { once: true });
    this.alertsDashboard?.addEventListener('openDialog', (event) => { event.stopPropagation(); this.openAlertsDashboardDialog() });
    this.alertsDashboard?.addEventListener('closeDialog', (event) => { event.stopPropagation(); this.closeAlertsDashboardDialog() });
  }

  /**
   * Apply the active state to the icon corresponding to the currently active application route
   */
  onRouteChange(): void {
    // TODO: Refactor
    let appName = window.location.pathname;
    appName = appName.replace(/^\//, '');
    appName = appName.split('?')[0];
    appName = appName.split('(')[0];

    /* istanbul ignore next else */
    if (!appName) {
      appName = appConfig.core ? appConfig.core.defaultRoute : undefined;
    }

    /* istanbul ignore next else */
    if (appName) {
      this.activateIcon(appName);
    }
  }

  /**
   * Handle app icon and more button clicks
   * @param event
   */
  onItemClick(event: MouseEvent): void {
    const item = event.currentTarget as HTMLElement;
    const itemClassList = Array.from(item.classList);
    const itemName = item.dataset.itemName;

    if (item.dataset.useCase) {
      let useCase: UseCase;
      try {
        useCase = JSON.parse(item.dataset.useCase);
      } catch (ex) {
        Logger.error(`Error parsing usecase data for ${itemName}`, ex);
      }
      if (useCase) {
        thirdPartyMediatorService.handleUseCase(useCase);
        this.activateIcon(itemName);
      }
    } else if (itemName === 'logout') {
      // TODO: remove after SWAT-4124 has been implemented
      event.preventDefault();
      thirdPartyMediatorService.logout();
    } else if (itemClassList.indexOf('feedback__button') !== -1) {
      thirdPartyMediatorService.openFeedbackApplication();
    } else if (itemName && itemName !== 'more' && item.getAttribute('target') !== '_blank') {
      router.navigateTo(itemName);
    }

    this.closeAlertsDashboardDialog()

    if (itemName === 'more') {
      this.toggleSubMenu();
    } else {
      this.toggleSubMenu(false);
    }

    if (item.dataset.perfTrackMark && !this.trackedApps[itemName]) {
      thirdPartyMediatorService.trackMark(item.dataset.perfTrackMark);
      this.trackedApps[itemName] = true;
    }

    const hasBadge = !!item.querySelector('[data-nav=menu-item-badge]');
    const isInSubMenu = this.querySelector(ACTIONS_MENU_HIDDEN_SELECTOR).contains(item);

    if (itemName) {
      this.trackItemClick(itemName, hasBadge, isInSubMenu);
    }
  }

  trackItemClick(name: string, hasBadge = false, isInSubMenu = false): void {
    tracklib.track({
      section: `navigator.${name}.jump`,
      trackingtype: 'cl',
      eventposition: isInSubMenu ? 'extended' : 'main',
      contentposition: hasBadge ? 'badger' : undefined, // 'badger' is not a typo
    });
  }

  activateIcon(appName: string): void {
    const prevActiveItem = document.querySelector(`.${ACTIVE_ITEM_CLASS}`);
    const activeIcon = document.querySelector(`[data-item-name=${appName}]`);

    if (prevActiveItem && activeIcon) {
      prevActiveItem.classList.remove(ACTIVE_ITEM_CLASS);
      prevActiveItem.classList.remove(ACTIVE_ITEM_MORE_ACTIVE_CLASS);
    }

    if (activeIcon) {
      const isInSubMenu = document.querySelector(ACTIONS_MENU_HIDDEN_SELECTOR).contains(activeIcon);
      if (!isInSubMenu) {
        activeIcon.classList.add(ACTIVE_ITEM_CLASS);
      }
    }
  }

  /**
   * Hide overflow menu items if they fit into the main menu
   */
  handleItems(): void {
    Array.from(this.querySelectorAll(`${ACTIONS_MENU_PRIMARY_SELECTOR} [data-item-name]`))
      .map((element) => element as HTMLElement)
      .forEach((element) => {
        if (element.offsetTop !== 0) {
          const itemName = element.getAttribute('data-item-name');
          const menuItem = this.querySelector(`.actions-menu__navigatoritem[data-item-name=${itemName}]`);
          /* istanbul ignore next else */
          if (menuItem) {
            menuItem.classList.remove('hidden');
            const mainMenuItem = this.querySelector(`${ACTIONS_MENU_PRIMARY_SELECTOR} [data-item-name=${itemName}]`);
            /* istanbul ignore next else */
            if (mainMenuItem) {
              mainMenuItem.setAttribute('tabindex', '-1');
            }
          }
        } else {
          const itemName = element.getAttribute('data-item-name');
          const menuItem = this.querySelector(`.actions-menu__navigatoritem[data-item-name=${itemName}]`);
          /* istanbul ignore next else */
          if (menuItem) {
            menuItem.classList.add('hidden');
            const mainMenuItem = this.querySelector(`${ACTIONS_MENU_PRIMARY_SELECTOR} [data-item-name=${itemName}]`);
            if (mainMenuItem) {
              mainMenuItem.removeAttribute('tabindex');
            }
          }
        }
      });

    this.handleGroups();
  }

  /**
   * hide submenu groups with no visible items
   */
  handleGroups(): void {
    const containers = Array.from(this.querySelectorAll('[data-nav-container=group]'));
    containers.forEach((container) => {
      container.classList.remove('hidden');
    });
    containers
      .filter((container) => {
        return container.querySelectorAll('.navigator__item:not(.hidden)').length === 0;
      })
      .forEach((container) => {
        container.classList.add('hidden');
      });
  }

  /**
   * close the sub menu/alerts dashboard when the modal layer is clicked
   * @param event
   */
  onModalLayerClick(event: CustomEvent): void {
    /* istanbul ignore next else */
    if (event.detail === false) {
      this.toggleSubMenu(false)
      this.closeAlertsDashboardDialog()
    }
  }

  /**
   * open/close the sub menu
   * @param open true: open the menu. false: close the menu. Any other value: toggle the menu
   */
  toggleSubMenu(open?: boolean): void {
    const openMenu = typeof open === 'boolean' ? open : !this.querySelector(`.${ACTIONS_MENU_HIDDEN_OPEN_CLASS}`)
    const activeNavItem = this.querySelector(`.${ACTIVE_ITEM_CLASS}`)

    if (openMenu) {
      activeNavItem?.classList.add(ACTIVE_ITEM_MORE_ACTIVE_CLASS)
      this.openSubMenu()
      !this.modalLayer.isVisible && this.modalLayer.show()
    } else {
      activeNavItem?.classList.remove(ACTIVE_ITEM_MORE_ACTIVE_CLASS)
      this.closeSubMenu()
      this.modalLayer.isVisible && this.modalLayer.hide()
    }
  }

  openSubMenu() {
    const moreButton = this.querySelector('[data-item-name=more]')
    const subMenu = this.querySelector(ACTIONS_MENU_HIDDEN_SELECTOR)
    moreButton?.classList.add(NAVIGATOR_ITEM_MORE_ACTIVE_CLASS)
    subMenu?.classList.add(ACTIONS_MENU_HIDDEN_OPEN_CLASS)
  }

  closeSubMenu() {
    const moreButton = this.querySelector('[data-item-name=more]')
    const subMenu = this.querySelector(ACTIONS_MENU_HIDDEN_SELECTOR)
    moreButton?.classList.remove(NAVIGATOR_ITEM_MORE_ACTIVE_CLASS)
    subMenu?.classList.remove(ACTIONS_MENU_HIDDEN_OPEN_CLASS)
  }

  showAlertsDashboardItem() {
    this.alertsDashboard?.classList.remove('navigator__item--hidden')
  }

  openAlertsDashboardDialog() {
    const activeNavItem = this.querySelector(`.${ACTIVE_ITEM_CLASS}`)
    this.alertsDashboardIsOpen = true

    this.closeSubMenu()

    activeNavItem?.classList.add(ACTIVE_ITEM_MORE_ACTIVE_CLASS)
    this.alertsDashboard?.classList.add(NAVIGATOR_ITEM_MORE_ACTIVE_CLASS)
    !this.modalLayer.isVisible && this.modalLayer.show()
  }

  closeAlertsDashboardDialog() {
    if (!this.alertsDashboardIsOpen) {
      return
    }

    const activeNavItem = this.querySelector(`.${ACTIVE_ITEM_CLASS}`)

    this.alertsDashboardIsOpen = false
    activeNavItem?.classList.remove(ACTIVE_ITEM_MORE_ACTIVE_CLASS)
    this.alertsDashboard?.classList.remove(NAVIGATOR_ITEM_MORE_ACTIVE_CLASS);
    // if the alerts dashboard JS has not been loaded, this function will be undefined
    if (typeof this.alertsDashboard?.closeAlertsDashboard === 'function') {
      this.alertsDashboard.closeAlertsDashboard()
    }
    this.modalLayer.isVisible && this.modalLayer.hide()
  }

}

window.customElements.define('nav-actions-menu', NavActionsMenu);
